import styled, { css } from 'styled-components'

export interface LabelStyleProps {
  variant?: 'default' | 'light' | 'dark'
  showPlaceholder: boolean
  hasError: boolean
}

export const StyledErrorMessage = styled.span`
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  margin-top: 0.6rem;
  padding-left: 0.6rem;
`

export const StyledSelect = styled.select`
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M6.092 7.73a.833.833 0 0 1 1.178.029L10 10.625l2.73-2.866a.833.833 0 1 1 1.207 1.15l-3.071 3.223-.013.014a1.205 1.205 0 0 1-1.705 0l-.013-.014-3.072-3.224a.833.833 0 0 1 .03-1.178Z' fill='%2330333A'/%3E%3C/svg%3E");
  background-position: right center;
  background-repeat: no-repeat;
  background-size: 1rem;
  border: 0;
  border-radius: 0.6rem;
  border-right: 0.6rem solid transparent;
  font-size: 0.9rem;
  height: 100%;
  line-height: 1.25;
  outline: 0;
  padding: 1.4rem 0.4rem 0.4rem 0.6rem;
  transition: all 0.2s;
  width: 100%;

  &:disabled {
    opacity: 0.5;
  }

  & + span {
    color: ${({ theme }) => theme.colors.black[1]};
    cursor: text;
    font-size: 0.7rem;
    font-weight: 600;
    left: 0.6rem;
    line-height: 1;
    pointer-events: none;
    position: absolute;
    top: 0.5rem;
    transition: all 0.2s;
  }

  &:disabled + span {
    opacity: 0.5;
  }

  option {
    color: ${({ theme }) => theme.colors.black[1]};
  }
`

export const StyledLabel = styled.label<LabelStyleProps>`
  display: block;
  position: relative;

  // Error message in case of error
  & + ${StyledErrorMessage} {
    ${({ hasError, variant }) =>
    hasError &&
    css`
        color: ${({ theme }) => (variant === 'dark' ? theme.colors.red[4] : theme.colors.red[3])};
      `}
  }

  & > ${StyledSelect} {
    ${({ showPlaceholder }) =>
    showPlaceholder &&
    css`
        &:not(:empty) + * {
          font-size: 0.9rem;
          font-weight: 400;
          top: 50%;
          transform: translateY(-50%);
        }
      `}

    // Error state
    ${({ hasError, variant }) =>
    hasError &&
    css`
        box-shadow: 0 0 0 1.5px ${({ theme }) => (variant === 'dark' ? theme.colors.red[4] : theme.colors.red[3])} !important;
      `}

    ${({ variant }) => {
    switch (variant) {
      case 'light':
        return css`
            background-color: ${({ theme }) => theme.colors.white[3]};
            color: ${({ theme }) => theme.colors.black[1]};

            &:hover {
              box-shadow: 0 0 0 1.5px ${({ theme }) => theme.colors.white[1]};
            }

            &:focus {
              box-shadow: 0 0 0 1.5px var(--c-theme-3);
            }

            // Large label
            ${({ showPlaceholder }) =>
            showPlaceholder &&
            css`
                &:not(:focus) + span {
                  opacity: 0.6;
                }
              `}

            // Small label
            & + span {
              opacity: 0.4;
            }
          `
      case 'dark':
        return css`
            background-color: rgba(0, 0, 0, 0.1);
            background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M6.092 7.73a.833.833 0 0 1 1.178.029L10 10.625l2.73-2.866a.833.833 0 0 1 1.207 1.15l-3.071 3.223-.014.014a1.204 1.204 0 0 1-1.705 0l-.013-.014-3.07-3.224a.833.833 0 0 1 .028-1.178Z' fill='%23fff'/%3E%3C/svg%3E");
            color: ${({ theme }) => theme.colors.white[1]};
            box-shadow: 0 0 0 1.5px ${({ theme }) => theme.colors.white[6]};

            &:hover {
              box-shadow: 0 0 0 1.5px ${({ theme }) => theme.colors.white[4]};

              & + span {
                color: ${({ theme }) => theme.colors.white[3]};
              }
            }

            &:focus {
              box-shadow: 0 0 0 1.5px ${({ theme }) => theme.colors.white[1]};

              & + span {
                color: ${({ theme }) => theme.colors.white[4]};
              }
            }

            // Large label
            ${({ showPlaceholder }) =>
            showPlaceholder &&
            css`
                &:not(:focus) + span {
                  color: ${({ theme }) => theme.colors.white[3]};
                }
              `}

            // Small label
            & + span {
              color: ${({ theme }) => theme.colors.white[4]};
            }
          `
      default:
        return css`
            background-color: ${({ theme }) => theme.colors.white[1]};
            color: ${({ theme }) => theme.colors.black[1]};
            box-shadow: 0 0 0 1.5px ${({ theme }) => theme.colors.black[6]};

            &:hover {
              box-shadow: 0 0 0 1.5px ${({ theme }) => theme.colors.black[5]};
            }

            &:focus {
              box-shadow: 0 0 0 1.5px ${({ theme }) => theme.colors.black[1]};
            }

            // Large label
            ${({ showPlaceholder }) =>
            showPlaceholder &&
            css`
                &:not(:focus) + span {
                  opacity: 0.6;
                }
              `}

            // Small label
            & + span {
              opacity: 0.4;
            }
          `
    }
  }}
  }
`
